import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    FormControlLabel,
    GridLegacy as Grid,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import type { Identifier, XYCoord } from "dnd-core";
import { FC, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";

const style = {
    marginBottom: ".5rem",
    cursor: "move",
};

export interface EventBlockCardProps {
    id: string;
    block: EventObject;
    index: number;
    moveBlock: (dragIndex: number, hoverIndex: number) => void;
    updateBlock: (block: EventObject) => void;
    deleteBlock: () => void;
    disabled?: boolean;
}

interface DragItem {
    index: number;
    id: string;
    type: string;
}

const typeLabels: Record<number, string> = {
    0: "Text",
    1: "Number",
    2: "Date",
    3: "Time",
    4: "Checkbox",
    5: "Dropdown",
    6: "Radio",
    7: "File",
    8: "Image",
    9: "Video",
    10: "Link",
    11: "Email",
    12: "Phone",
    13: "Address",
    14: "Name",
    15: "Age Group",
    16: "Notes",
    17: "Custom",
    18: "Unit",
};

// Block type constants for clarity
const BlockTypes = {
    TEXT: 0,
    NUMBER: 1,
    DROPDOWN: 5,
    CHECKBOX: 4,
    EMAIL: 11,
    PHONE: 12,
    NAME: 14,
    GROUP: 15,
    NOTES: 16,
    CUSTOM: 17,
    UNIT: 18,
};

// Special block types that have predefined behavior
const specialBlockTypes = [
    BlockTypes.EMAIL,
    BlockTypes.PHONE,
    BlockTypes.NAME,
    BlockTypes.GROUP,
    BlockTypes.NOTES,
    BlockTypes.UNIT,
];

export const EventBlockCard: FC<EventBlockCardProps> = ({ id, block, index, moveBlock, updateBlock, deleteBlock, disabled }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
        accept: "eventBlock",
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item: DragItem, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveBlock(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: "eventBlock",
        item: () => {
            return { id, index };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        canDrag: !disabled,
    });

    const opacity = isDragging ? 0.4 : 1;
    preview(drop(ref));

    const isSpecialType = specialBlockTypes.includes(block.type);

    return (
        <Paper
            ref={ref}
            style={{ ...style, opacity }}
            data-handler-id={handlerId}
            onContextMenu={(ev) => {
                ev.preventDefault();
                if (!disabled) {
                    setAnchorEl(ev.currentTarget);
                }
            }}
        >
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem
                    onClick={() => {
                        setAnchorEl(null);
                        deleteBlock();
                    }}
                >
                    <ListItemIcon>
                        <DeleteForeverIcon />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>
            <Accordion
                expanded={expanded}
                onChange={(_, isExpanded) => setExpanded(isExpanded)}
                sx={{ backgroundColor: block.enabled ? undefined : "action.disabledBackground" }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                        <div ref={drag as any} style={{ display: "flex" }}>
                            <IconButton
                                size="small"
                                sx={{ cursor: disabled ? "not-allowed" : "grab" }}
                                disabled={disabled}
                            >
                                <DragIndicatorIcon />
                            </IconButton>
                        </div>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={block.enabled}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        updateBlock({ ...block, enabled: e.target.checked });
                                    }}
                                    disabled={disabled}
                                />
                            }
                            label=""
                            sx={{ mr: 0 }}
                        />
                        <Typography sx={{ flexGrow: 1 }}>
                            {block.title}
                            {block.required && <span style={{ color: "red" }}> *</span>}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {typeLabels[block.type] || "Unknown"}
                        </Typography>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Title"
                                value={block.title}
                                onChange={(e) => updateBlock({ ...block, title: e.target.value })}
                                fullWidth
                                variant="standard"
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Select
                                value={block.type}
                                onChange={(e) => updateBlock({ ...block, type: Number(e.target.value) })}
                                fullWidth
                                variant="standard"
                                disabled={disabled || isSpecialType}
                            >
                                <MenuItem value={BlockTypes.TEXT}>Text</MenuItem>
                                <MenuItem value={BlockTypes.NUMBER}>Number</MenuItem>
                                <MenuItem value={BlockTypes.DROPDOWN}>Dropdown</MenuItem>
                                <MenuItem value={BlockTypes.CHECKBOX}>Checkbox</MenuItem>
                                {isSpecialType && <MenuItem value={block.type}>{typeLabels[block.type]}</MenuItem>}
                            </Select>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={block.required}
                                        onChange={(e) => updateBlock({ ...block, required: e.target.checked })}
                                        disabled={disabled}
                                    />
                                }
                                label="Required"
                            />
                        </Grid>
                        {(block.type === BlockTypes.DROPDOWN || block.type === 6) && ( // Dropdown or Radio
                            <Grid item xs={12}>
                                <TextField
                                    label="Options (comma-separated)"
                                    value={block.options || ""}
                                    onChange={(e) => updateBlock({ ...block, options: e.target.value })}
                                    fullWidth
                                    variant="standard"
                                    helperText="Enter options separated by commas"
                                    disabled={disabled}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Default Value"
                                value={block.default || ""}
                                onChange={(e) => updateBlock({ ...block, default: e.target.value })}
                                fullWidth
                                variant="standard"
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Helper Text"
                                value={block.helper_text || ""}
                                onChange={(e) => updateBlock({ ...block, helper_text: e.target.value })}
                                fullWidth
                                variant="standard"
                                disabled={disabled}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Paper>
    );
};
